import { featureFlags } from '@shared-services/feature-flags';
import { useEditorContext } from 'client/infra/renderers/editorState';
import { getWindowInBrowser } from 'client/RuntimeWindow';
import usePrevious from 'client/widget-components/customHooks/usePrevious';
import { AutoPaginationProps } from 'client/widgets/runtime-slider/sliderCommonTypes';
import { useEffect, useRef, useState } from 'react';

export const NO_TOTAL_PAGES_ERROR =
    'The UsePagination hook must receive a totalPages argument for it to work';

export const useSliderPagination = ({
    totalPages,
    selectedIndex = 0,
    autoPagination,
    jumpThreshold = 5,
}: {
    totalPages: number;
    selectedIndex?: number | undefined;
    autoPagination?: AutoPaginationProps;
    jumpThreshold?: number;
}) => {
    const [isLargeJumpEnabled] = useState(() =>
        featureFlags.getBoolean(
            'runtime.ssr.ssrSlider.jumpThreshold.enabled',
            false
        )
    );
    const [cursor, setCursor] = useState(selectedIndex);
    const [newEnters, setNewEnters] = useState(0);
    const [hovered, setHovered] = useState(false);
    const [shouldSuppressAnimation, setShouldSuppressAnimation] =
        useState(false);
    const timerInterval = useRef<number>();
    const { isInEditor } = useEditorContext();
    const previousCursor = usePrevious(cursor);

    const isLargeJump =
        isLargeJumpEnabled &&
        (shouldSuppressAnimation ||
            checkIsLargeJump(previousCursor ?? cursor, cursor, jumpThreshold));

    useEffect(() => {
        setShouldSuppressAnimation(true);
        setCursor(selectedIndex);
    }, [selectedIndex]);

    const goNext = () => {
        setShouldSuppressAnimation(false);
        setCursor((currentCursor) => {
            const newCursorCandidate = currentCursor + 1;
            return newCursorCandidate === totalPages ? 0 : newCursorCandidate;
        });
        setNewEnters((n) => n - 1);
    };

    const goPrevious = () => {
        setShouldSuppressAnimation(false);
        setCursor((currentCursor) => {
            const newCursorCandidate = currentCursor - 1;
            return newCursorCandidate === -1
                ? totalPages - 1
                : newCursorCandidate;
        });
        setNewEnters((n) => n + 1);
    };

    const goToCursor = (newCursor: number) => {
        setShouldSuppressAnimation(true);
        setCursor(newCursor);
        setNewEnters(cursor - newCursor);
    };

    const restartAutoPagination = () => {
        if (autoPagination?.on) {
            clearInterval(timerInterval.current);
            timerInterval.current = getWindowInBrowser().setInterval(() => {
                goNext();
            }, autoPagination.intervalInSeconds * 1000);
        }
    };

    useEffect(() => {
        // I could not find a way to optimize nested conditions. I will leave it as is.
        if (hovered) {
            if (!isInEditor) {
                if (!autoPagination?.pauseOnHover) {
                    restartAutoPagination();
                }
            }
        } else {
            restartAutoPagination();
        }

        return () => {
            clearInterval(timerInterval.current);
        };
    }, [autoPagination, hovered]);

    return {
        cursor,
        restartAutoPagination,
        newEnters,
        suppressAnimation: isLargeJump,
        paginationAction: {
            goToCursor,
            goNext,
            goPrevious,
        },
        onmouseenter: () => {
            setHovered(true);
        },
        onmouseleave: () => {
            setHovered(false);
        },
        onPageTransitionCompleted: () => {
            setNewEnters(0);
            setShouldSuppressAnimation(false);
        },
    };
};

const checkIsLargeJump = (
    fromCursor: number,
    toCursor: number,
    jumpThreshold: number
): boolean => {
    const distance = Math.abs(fromCursor - toCursor);
    return distance > jumpThreshold;
};
